import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Stack,
    TextField,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import {ArrowBack, Edit, Lock, LockOpen, RestartAlt} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheckSquare, faServer, faEnvelope, faEnvelopeOpen} from '@fortawesome/pro-solid-svg-icons';

import {useApplicationContext} from '../../ApplicationContext';
import {useDocumentTitle} from '../../Hooks/useDocumentTitle';
import {GetSupportUserData, GetUserData, MfaStatusDto} from '../../apiClient/Api';
import {AddUserComponent} from '../../Components/Admin/User/AddUserComponent';
import {DeleteUserDialog} from '../../Components/Admin/User/DeleteUserDialog';

const NarrowListItem = styled(ListItem)({paddingTop: 0, paddingBottom: 0});

const userGroupLabel = (group: number | undefined) => {
    switch (group) {
        case 0: return 'Super Admin';
        case 1: return 'Admin';
        case 2: return 'Entwickler';
        case 3: return 'Büro';
        case 4: return 'Kunde';
        default: return 'Unbekannt';
    }
};

export const AdminUserDetail = () => {
    useDocumentTitle('base.pages.users');
    const {t} = useTranslation();
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const context = useApplicationContext();

    const [user, setUser] = useState<GetUserData | undefined>(undefined);
    const [supportData, setSupportData] = useState<GetSupportUserData | undefined>(undefined);
    const [mfaStatus, setMfaStatus] = useState<MfaStatusDto | undefined>(undefined);
    const [loading, setLoading] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
    const [mfaResetConfirmOpen, setMfaResetConfirmOpen] = useState(false);

    const [labelUserValue, setLabelUserValue] = useState('');
    const [labelUserDirty, setLabelUserDirty] = useState(false);

    const loadAll = useCallback(() => {
        if (!id) return;
        setLoading(true);
        Promise.all([
            context.api.user.userGetUser(id),
            context.api.user.userGetSupportUserData(id),
            context.api.user.userGetMfaStatusForUser(id),
        ]).then(([userRes, supportRes, mfaRes]) => {
            setUser(userRes.data);
            setSupportData(supportRes.data);
            setMfaStatus(mfaRes.data);
            setLabelUserValue(userRes.data.labelUser ?? '');
            setLabelUserDirty(false);
        }).catch(context.apiError).finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleToggleLock = () => {
        if (!id) return;
        context.api.user.userLockUser(id).then(() => loadAll()).catch(context.apiError);
    };

    const handleEditClosed = (saved: boolean) => {
        setEditOpen(false);
        if (saved) loadAll();
    };

    const handleDeleted = (deleted: boolean) => {
        setDeleteOpen(false);
        if (deleted) navigate('/admin/users');
    };

    const handleResend = () => {
        if (!id) return;
        if (user?.lastLogin == undefined) {
            context.api.user.userResendFirstLogin(id).then((res) => {
                if (res.data) alert(t('adminUsers.dataSentToUser'));
            }).catch(context.apiError);
        } else {
            setResendConfirmOpen(true);
        }
    };

    const handleResendConfirmed = () => {
        if (!id) return;
        context.api.user.userResendPasswordReset(id).then((res) => {
            if (res.data) alert(t('adminUsers.dataSentToUser'));
            setResendConfirmOpen(false);
        }).catch(context.apiError);
    };

    const handleLabelUserSave = () => {
        if (!id) return;
        context.api.user.userSetLabelUser(id, {labelUser: labelUserValue.trim() || null})
            .then(() => {
                setLabelUserDirty(false);
                loadAll();
            })
            .catch(context.apiError);
    };

    const handleMfaReset = () => {
        if (!id) return;
        context.api.user.userAdminResetMfa(id)
            .then(() => {
                setMfaResetConfirmOpen(false);
                loadAll();
            })
            .catch(context.apiError);
    };

    if (loading && !user) {
        return <Box sx={{display: 'flex', justifyContent: 'center', mt: 6}}><CircularProgress/></Box>;
    }
    if (!user) return null;

    const hasAnyFactor = (mfaStatus?.totpEnabled ?? false) || (mfaStatus?.passkeyCount ?? 0) > 0;

    return (
        <Box sx={{p: 2, maxWidth: 900, mx: 'auto'}}>
            <AddUserComponent open={editOpen} closeComponent={handleEditClosed} userId={id}/>
            <DeleteUserDialog id={deleteOpen ? id : undefined} userDeleted={handleDeleted}/>

            <Dialog open={resendConfirmOpen} onClose={() => setResendConfirmOpen(false)}>
                <DialogTitle>{t('adminUsers.resendConfirmTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('adminUsers.resendConfirmMessage', {email: user.eMail})}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResendConfirmOpen(false)} color="inherit">{t('adminUsers.cancel')}</Button>
                    <Button onClick={handleResendConfirmed} color="primary" variant="contained" autoFocus>{t('adminUsers.send')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={mfaResetConfirmOpen} onClose={() => setMfaResetConfirmOpen(false)}>
                <DialogTitle>2FA zurücksetzen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        TOTP und alle Passkeys von {user.name} ({user.eMail}) werden entfernt. Der Nutzer muss sich
                        beim nächsten Login neu registrieren, falls die Rolle 2FA verlangt.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMfaResetConfirmOpen(false)} color="inherit">{t('adminUsers.cancel')}</Button>
                    <Button onClick={handleMfaReset} color="error" variant="contained" autoFocus>2FA zurücksetzen</Button>
                </DialogActions>
            </Dialog>

            <Stack direction="row" spacing={1} sx={{alignItems: 'center', mb: 2}}>
                <IconButton onClick={() => navigate('/admin/users')}><ArrowBack/></IconButton>
                <Typography variant="h5" sx={{flexGrow: 1}}>{user.name}</Typography>
                <Chip label={userGroupLabel(user.userGroup)} size="small"/>
                <Chip label={user.locked ? 'Gesperrt' : 'Aktiv'} color={user.locked ? 'error' : 'success'} size="small"/>
            </Stack>

            <Stack direction="row" spacing={1} sx={{mb: 2, flexWrap: 'wrap', gap: 1}}>
                <Tooltip title={t('adminUsers.editUser')}>
                    <Button startIcon={<Edit/>} onClick={() => setEditOpen(true)}>{t('adminUsers.editUser')}</Button>
                </Tooltip>
                <Tooltip title={!user.locked ? t('adminUsers.lockUser') : t('adminUsers.unlockUser')}>
                    <Button startIcon={!user.locked ? <Lock/> : <LockOpen/>} onClick={handleToggleLock}>
                        {!user.locked ? t('adminUsers.lockUser') : t('adminUsers.unlockUser')}
                    </Button>
                </Tooltip>
                <Tooltip title={user.lastLogin == undefined ? t('adminUsers.sendLoginData') : t('adminUsers.userAlreadyLoggedIn')}>
                    <Button
                        startIcon={<FontAwesomeIcon icon={user.lastLogin == undefined ? faEnvelope : faEnvelopeOpen}/>}
                        onClick={handleResend}
                    >
                        {user.lastLogin == undefined ? t('adminUsers.sendLoginData') : t('adminUsers.userAlreadyLoggedIn')}
                    </Button>
                </Tooltip>
                <Tooltip title={t('adminUsers.deleteUser')}>
                    <Button color="error" startIcon={<DeleteIcon/>} onClick={() => setDeleteOpen(true)}>
                        {t('adminUsers.deleteUser')}
                    </Button>
                </Tooltip>
            </Stack>

            <Card variant="outlined" sx={{mb: 2}}>
                <CardContent>
                    <Typography variant="h6" sx={{mb: 1}}>Stammdaten</Typography>
                    <List dense disablePadding>
                        <NarrowListItem><ListItemText primary={user.eMail} secondary="Mail"/></NarrowListItem>
                        <NarrowListItem>
                            <ListItemText
                                primary={dayjs(user.created).format('DD.MM.YYYY HH:mm')}
                                secondary="Erstellt"
                            />
                        </NarrowListItem>
                        <NarrowListItem>
                            <ListItemText
                                primary={user.lastLogin == undefined ? '–' : dayjs(user.lastLogin.toString()).format('DD.MM.YYYY HH:mm:ss')}
                                secondary="Zuletzt eingeloggt"
                            />
                        </NarrowListItem>
                        <NarrowListItem>
                            <ListItemText
                                primary={user.userPrincipalName ?? '– (lokales Konto)'}
                                secondary="AD-Konto (UserPrincipalName)"
                            />
                        </NarrowListItem>
                        <NarrowListItem>
                            <ListItemText
                                primary={supportData?.acceptedTerms == undefined
                                    ? '-'
                                    : `${supportData.acceptedTerms.versionTitle} (${dayjs(supportData.acceptedTerms.created).format('DD.MM.YYYY')})`}
                                secondary="Akzeptierte Bedingungen"
                            />
                        </NarrowListItem>
                    </List>

                    <Divider sx={{my: 2}}/>

                    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                        <TextField
                            size="small"
                            label={t('adminUsers.labelUser')}
                            value={labelUserValue}
                            onChange={(e) => {
                                setLabelUserValue(e.target.value);
                                setLabelUserDirty(true);
                            }}
                            helperText={t('adminUsers.labelUserHint')}
                        />
                        <Button
                            variant="contained"
                            disabled={!labelUserDirty}
                            onClick={handleLabelUserSave}
                            sx={{alignSelf: 'flex-start'}}
                        >
                            {t('adminUsers.save')}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            <Card variant="outlined" sx={{mb: 2}}>
                <CardContent>
                    <Typography variant="h6" sx={{mb: 1}}>Zwei-Faktor-Authentifizierung</Typography>
                    {mfaStatus == undefined ? <CircularProgress size={20}/> : (
                        <>
                            <List dense disablePadding>
                                <NarrowListItem>
                                    <ListItemText primary={mfaStatus.totpEnabled ? 'Aktiv' : 'Nicht eingerichtet'} secondary="TOTP"/>
                                </NarrowListItem>
                                <NarrowListItem>
                                    <ListItemText primary={mfaStatus.passkeyCount ?? 0} secondary="Registrierte Passkeys"/>
                                </NarrowListItem>
                                <NarrowListItem>
                                    <ListItemText primary={mfaStatus.remainingRecoveryCodes ?? 0} secondary="Verbleibende Recovery-Codes"/>
                                </NarrowListItem>
                                <NarrowListItem>
                                    <ListItemText primary={mfaStatus.mfaRequiredByRole ? 'Ja' : 'Nein'} secondary="Durch Rolle vorgeschrieben"/>
                                </NarrowListItem>
                            </List>

                            {mfaStatus.mfaRequiredByRole && !hasAnyFactor && (
                                <Alert severity="warning" sx={{mt: 2}}>
                                    Die Rolle dieses Nutzers verlangt 2FA, es ist aber noch kein Faktor registriert.
                                    Beim nächsten Login wird der Nutzer zur Einrichtung aufgefordert.
                                </Alert>
                            )}

                            <Button
                                sx={{mt: 2}}
                                color="error"
                                variant="outlined"
                                startIcon={<RestartAlt/>}
                                disabled={!hasAnyFactor}
                                onClick={() => setMfaResetConfirmOpen(true)}
                            >
                                2FA zurücksetzen
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>

            {supportData?.supportContracts?.map((contract) => (
                <Card variant="outlined" sx={{mb: 2}} key={contract.contractId}>
                    <CardContent>
                        <Typography variant="h6" sx={{mb: 1}}>Wartungsvertrag {contract.title}</Typography>
                        <List dense disablePadding>
                            <NarrowListItem><ListItemText primary={contract.customerName} secondary="Kunde"/></NarrowListItem>
                            <NarrowListItem><ListItemText primary={contract.customerId} secondary="Kundennummer"/></NarrowListItem>
                            <NarrowListItem><ListItemText primary={contract.contractId} secondary="Vertragsnummer"/></NarrowListItem>
                            <NarrowListItem><ListItemText primary={contract.comment} secondary="Beschreibung"/></NarrowListItem>
                            <NarrowListItem><ListItemText primary={contract.infinite ? 'Ja' : 'Nein'} secondary="Unbegrenzte Laufzeit"/></NarrowListItem>
                        </List>
                        <ListSubheader>FilterTags</ListSubheader>
                        <List>
                            {contract.filterTags?.map((tag) => (
                                <NarrowListItem key={tag.name}>
                                    <ListItemIcon><FontAwesomeIcon icon={faCheckSquare}/></ListItemIcon>
                                    <ListItemText>{tag.name}</ListItemText>
                                </NarrowListItem>
                            ))}
                        </List>
                        <ListSubheader>Systeme</ListSubheader>
                        <List>
                            {contract.supportCodes?.map((code) => (
                                <NarrowListItem key={code.id}>
                                    <ListItemIcon><FontAwesomeIcon icon={faServer}/></ListItemIcon>
                                    <ListItemText primary={code.name} secondary={code.id}/>
                                </NarrowListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
};

export default AdminUserDetail;
